/**
 * Team Management API Service
 * Handles all team-related API calls
 */

import { apiClient } from './client';
import type { Team } from '@repo/types';

// API request/response types matching backend
export interface CreateTeamRequest {
  name: string;
  leadId?: string;
}

export interface UpdateTeamRequest {
  name?: string;
  leadId?: string | null;
}

export interface PaginatedTeamsResponse {
  data: Team[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  joinedAt: Date;
}

export interface AssignEmployeeRequest {
  employeeId: string;
  teamId: string | null;
}

/**
 * Team API service class
 */
export class TeamApiService {
  /**
   * Get all teams with pagination
   */
  async getTeams(page = 1, limit = 20) {
    return apiClient.get<PaginatedTeamsResponse>(`/teams?page=${page}&limit=${limit}`);
  }

  /**
   * Get team by ID
   */
  async getTeamById(id: string) {
    return apiClient.get<Team>(`/teams/${id}`);
  }

  /**
   * Create new team
   */
  async createTeam(data: CreateTeamRequest) {
    return apiClient.post<Team>('/teams', data);
  }

  /**
   * Update team
   */
  async updateTeam(id: string, data: UpdateTeamRequest) {
    return apiClient.put<Team>(`/teams/${id}`, data);
  }

  /**
   * Delete team
   */
  async deleteTeam(id: string) {
    return apiClient.delete(`/teams/${id}`);
  }

  /**
   * Get team members
   */
  async getTeamMembers(teamId: string) {
    return apiClient.get<TeamMember[]>(`/teams/${teamId}/members`);
  }

  /**
   * Assign employee to team
   */
  async assignEmployee(data: AssignEmployeeRequest) {
    return apiClient.post('/teams/assign', data);
  }
}

// Export singleton instance
export const teamApi = new TeamApiService();
